import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Box } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useCollapseDrawer from 'common/hooks/useCollapseDrawer';
import { FC } from 'react';

type Props = {
  goalSummary: {
    dailyCompletedLessons: number;
    weeklyCompletedLessons: number;
    dailyGoal: number;
    weeklyGoal: number;
    personalDailyGoal: number | null | undefined;
    personalWeeklyGoal: number | null | undefined;
  };
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: 1,
  gap: '1rem',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'end',
  marginRight: '2rem',
  [theme.breakpoints.down('xl')]: {
    marginRight: '1rem',
  },
  [theme.breakpoints.down('lg')]: {
    marginRight: '0rem',
    flexDirection: 'column',
    justifyContent: 'center',
  },
}));

const GoalContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  fontSize: '14px',
  '&:hover': {
    opacity: '0.6',
  },
  transition: 'opacity 0.3s',
  cursor: 'pointer',
  [theme.breakpoints.down('xl')]: {
    fontSize: '13px',
    gap: '8px',
  },
}));

const BoldText = styled('span')(({ theme }) => ({
  fontWeight: 'bold',
}));

const Separator = styled('div')(({ theme }) => ({
  borderRight: '1px solid #ccc',
  opacity: '0.75',
  height: '3rem',
  width: '1px',
  [theme.breakpoints.down('xl')]: {
    display: 'none',
  },
}));

const CustomBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '0.5rem',
  justifyContent: 'start',
  alignItems: 'center',
  marginLeft: '0.5rem',
  [theme.breakpoints.down('xl')]: {
    marginLeft: '0',
  },
}));

const RecommendationTitleSummary: FC<Props> = ({
  goalSummary: {
    dailyCompletedLessons,
    weeklyCompletedLessons,
    dailyGoal,
    weeklyGoal,
    personalDailyGoal,
    personalWeeklyGoal,
  },
}: Props) => {
  const { collapseClick } = useCollapseDrawer();
  const theme = useTheme();
  const iconStyles = () => ({
    [theme.breakpoints.down('xl')]: {
      fontSize: '20px',
    },
    [theme.breakpoints.down('lg')]: {
      fontSize: '15px',
    },
  });
  const dailyGoals = personalDailyGoal ?? dailyGoal;
  const weeklyGoals = personalWeeklyGoal ?? weeklyGoal;
  return (
    <Container
      sx={{
        flexDirection: window.innerWidth < 1300 && !collapseClick ? 'column' : 'row',
      }}
    >
      <GoalContainer>
        <TimerOutlinedIcon sx={iconStyles} />
        <BoldText>{personalDailyGoal ? 'Personal Goal:' : "Today's Goal:"} </BoldText>
        {dailyGoals} Lessons
      </GoalContainer>
      <Separator />
      <GoalContainer>
        <CalendarMonthOutlinedIcon sx={iconStyles} />
        <BoldText>{personalWeeklyGoal ? 'Personal Weekly Goals:' : 'Weekly Goals:'}</BoldText>
        {weeklyGoals} Lessons
      </GoalContainer>
      <Separator />
      <GoalContainer>
        <AddTaskOutlinedIcon sx={iconStyles} />
        <BoldText>Completed: </BoldText>
        <CustomBox>
          <span>
            {dailyCompletedLessons} / {dailyGoals}
          </span>{' '}
          <BoldText>{' today'}</BoldText>
          <div
            style={{
              borderRight: '1px solid #ccc',
              opacity: '0.75',
              height: '1.5rem',
              width: '1px',
            }}
          />
          <div>
            {weeklyCompletedLessons} / {weeklyGoals}
          </div>{' '}
          <BoldText>{' weekly'}</BoldText>
        </CustomBox>
      </GoalContainer>
    </Container>
  );
};

export default RecommendationTitleSummary;
